/**
 * Utility functions for handling markdown conversion
 */

// Simple utility to detect if content is likely markdown
export const isLikelyMarkdown = content => {
    if (!content || typeof content !== 'string') return false

    // Check for markdown patterns
    const markdownPatterns = [
        /\*\*(.+?)\*\*/, // Bold
        /^#\s.+$/m, // Heading
        /^\s*[-*]\s.+$/m, // Unordered list
        /^\s*\d+\.\s.+$/m, // Ordered list
    ]

    return markdownPatterns.some(pattern => pattern.test(content))
}

/**
 * Converts markdown text to HTML, supporting only:
 * - Headings (# Header)
 * - Bold (**bold**)
 * - Unordered lists (- item)
 * - Ordered lists (1. item)
 *
 * @param {string} markdown - Markdown text to convert
 * @returns {string} HTML representation of the markdown
 */
export const convertMarkdownToHtml = markdown => {
    if (!markdown) return ''

    let html = markdown

    // Convert headings (# Heading)
    html = html.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
    html = html.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
    html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')

    // Convert bold (**text**)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

    // First, process and mark unordered and ordered lists separately
    // to avoid confusing them during the replacement

    // Process unordered lists (with a specific marker for ul items)
    let processedHtml = html
    const ulMatches = html.match(/^\s*[-*]\s+(.+)$/gm)
    if (ulMatches) {
        processedHtml = html.replace(/^\s*[-*]\s+(.+)$/gm, '<!--UL_ITEM--><li>$1</li>')
    }

    // Process ordered lists (with a specific marker for ol items)
    const olMatches = processedHtml.match(/^\s*\d+\.\s+(.+)$/gm)
    if (olMatches) {
        processedHtml = processedHtml.replace(/^\s*\d+\.\s+(.+)$/gm, '<!--OL_ITEM--><li>$1</li>')
    }

    // Now wrap the list items in appropriate list containers
    // Wrap unordered list items
    processedHtml = processedHtml.replace(/(<!--UL_ITEM--><li>.+<\/li>(\n|$))+/g, '<ul>$&</ul>')

    // Wrap ordered list items
    processedHtml = processedHtml.replace(/(<!--OL_ITEM--><li>.+<\/li>(\n|$))+/g, '<ol>$&</ol>')

    // Remove the markers
    processedHtml = processedHtml.replace(/<!--UL_ITEM-->/g, '')
    processedHtml = processedHtml.replace(/<!--OL_ITEM-->/g, '')

    // Mark paragraphs with a special class that we can identify later
    // Split by double newlines (paragraph breaks)
    const paragraphs = processedHtml.split(/\n\n+/)

    // Process each paragraph and wrap it
    processedHtml = paragraphs
        .map(para => {
            // Skip if paragraph is already wrapped in a tag (like h1, ul, ol)
            if (para.match(/^<[a-z][^>]*>/i) && para.match(/<\/[a-z][^>]*>$/i)) {
                return para
            }
            // Otherwise wrap it in a span with a special class for later processing
            return `<span class="md-paragraph">${para}</span>`
        })
        .join('')

    return processedHtml
}
